import { AppConstants } from '@/app.constants';
import InspectionStatus from '@/enums/InspectionStatus.enum';
import { ControlPoint } from '@/types/ControlPoint';
import { Inspection } from '@/types/Inspection.type';
import {
  FactoryVerificationStep,
  FactoryVisitSession,
  FactoryVisitSessionEvidence,
  GpsCaptureSource,
  LocationEvidence,
  LocationEvidenceRole,
  LocationVerificationDraft,
} from '@/types/LocationVerification.type';
import { isInspectionStatus } from '@/utils/inspectionStart.utils';
import { DeviceLocationFix } from '@/utils/captureBestDeviceLocation';
import { isFactoryVisitSessionReuseEnabled } from '@/utils/factoryVisitSession.util';
import { haversineDistanceMetres } from '@/utils/geoDistance';
import {
  DeviceLocation,
  getFactoryRegisteredCoords,
} from '@/utils/startInspectionGpsVerification.utils';
import { isUuid } from '@/utils/string.util';

export function resolveServerFactoryVisitSessionId(
  session: FactoryVisitSession | null | undefined
): string | undefined {
  if (session?.serverSessionId && isUuid(session.serverSessionId)) {
    return session.serverSessionId;
  }
  return undefined;
}

export function resolveEvidenceFactoryVisitSessionId(
  evidence: Pick<LocationEvidence, 'factoryVisitSessionId'>,
  session?: FactoryVisitSession | null
): string | undefined {
  if (evidence.factoryVisitSessionId && isUuid(evidence.factoryVisitSessionId)) {
    return evidence.factoryVisitSessionId;
  }
  return resolveServerFactoryVisitSessionId(session);
}

export function controlPointRequiresLocationVerification(
  controlPoint?: Pick<ControlPoint, 'name'> | null
): boolean {
  const name = controlPoint?.name?.trim();
  if (!name) {
    return false;
  }
  return AppConstants.LOCATION_VERIFICATION_CONTROL_POINT_NAMES.some(
    (controlPointName) => controlPointName === name
  );
}

export function inspectionRequiresLocationVerification(inspection: Inspection | null): boolean {
  return controlPointRequiresLocationVerification(inspection?.controlPoint);
}

export function getVisibleEvidence(
  draft: LocationVerificationDraft | null | undefined
): LocationEvidence[] {
  return draft?.evidence?.filter((item) => item.hasPhoto !== false) ?? [];
}

export function hasEvidenceForRole(
  draft: LocationVerificationDraft | null | undefined,
  role: LocationEvidenceRole
): boolean {
  return getVisibleEvidence(draft).some((item) => item.role === role);
}

export function locationEvidenceHasGpsCoordinates(
  evidence: Pick<LocationEvidence, 'latitude' | 'longitude'>
): boolean {
  return evidence.latitude != null && evidence.longitude != null;
}

export function applyGpsFixToEvidence(
  evidence: LocationEvidence,
  fix: DeviceLocationFix
): LocationEvidence {
  return {
    ...evidence,
    latitude: fix.latitude,
    longitude: fix.longitude,
    accuracy: fix.accuracyMetres,
    altitude: fix.altitude,
    altitudeAccuracy: fix.altitudeAccuracy,
    heading: fix.heading,
    speed: fix.speed,
    gpsCaptureSource: fix.gpsCaptureSource,
    gpsAccuracyPreciseEnough: fix.gpsAccuracyPreciseEnough,
    connectionType: fix.connectionType,
    connectionEffectiveType: fix.connectionEffectiveType,
    geolocationPermissionState: fix.geolocationPermissionState,
    clientUserAgent: fix.clientUserAgent,
    gpsCapturedAt: fix.gpsCapturedAt,
  };
}

export function verificationRoleEvidenceHasGpsCoordinates(
  draft: LocationVerificationDraft | null | undefined,
  role: LocationEvidenceRole
): boolean {
  const items = getEvidenceForRole(draft, role).filter((item) => item.hasPhoto !== false);
  if (items.length === 0) {
    return false;
  }
  return items.every(locationEvidenceHasGpsCoordinates);
}

export function verificationPhotosHaveRequiredGps(
  draft: LocationVerificationDraft | null | undefined
): boolean {
  return (
    verificationRoleEvidenceHasGpsCoordinates(draft, 'ENTRANCE') &&
    verificationRoleEvidenceHasGpsCoordinates(draft, 'INSPECTION_ROOM')
  );
}

export function isVerificationProgressCommitted(
  draft: LocationVerificationDraft | null | undefined
): boolean {
  if (!draft) {
    return false;
  }
  return draft.isProgressCommitted !== false;
}

export function hasUnsavedEntranceProgress(
  draft: LocationVerificationDraft | null | undefined
): boolean {
  return hasEvidenceForRole(draft, 'ENTRANCE') && !isVerificationProgressCommitted(draft);
}

export function getEvidenceForRole(
  draft: LocationVerificationDraft | null | undefined,
  role: LocationEvidenceRole
): LocationEvidence[] {
  return getVisibleEvidence(draft).filter((item) => item.role === role);
}

export function hasPendingEvidenceUpload(
  draft: LocationVerificationDraft | null | undefined
): boolean {
  return Boolean(
    draft?.evidence?.some((item) => item.uploadState === 'PENDING' && item.hasPhoto !== false)
  );
}

export function isVerificationDraftComplete(
  draft: LocationVerificationDraft | null | undefined
): boolean {
  return hasEvidenceForRole(draft, 'ENTRANCE') && hasEvidenceForRole(draft, 'INSPECTION_ROOM');
}

export function hasCompleteReusableFactorySession(
  session: FactoryVisitSession | null | undefined
): boolean {
  if (!session || session.status !== 'ACTIVE') {
    return false;
  }
  return sessionHasRole(session, 'ENTRANCE') && sessionHasRole(session, 'INSPECTION_ROOM');
}

export function hasInProgressVerificationDraft(
  draft: LocationVerificationDraft | null | undefined
): boolean {
  if (!draft?.evidence?.length) {
    return false;
  }
  if (draft.workflowState === 'STARTED') {
    return false;
  }
  return !isVerificationDraftComplete(draft);
}

export function canAutoSkipFactoryVerification(params: {
  draft: LocationVerificationDraft | null | undefined;
  session: FactoryVisitSession | null | undefined;
  reuseDays?: number;
}): boolean {
  const { draft, session, reuseDays } = params;
  const canReuse = reuseDays == null || isFactoryVisitSessionReuseEnabled(reuseDays);
  if (!canReuse || !hasCompleteReusableFactorySession(session)) {
    return false;
  }
  if (hasInProgressVerificationDraft(draft)) {
    return false;
  }
  if (isVerificationDraftComplete(draft)) {
    return false;
  }
  return true;
}

export function buildAutoReuseDraft(params: {
  draft: LocationVerificationDraft;
  session: FactoryVisitSession;
}): LocationVerificationDraft {
  return copyReusableSessionEvidenceToDraft({
    draft: params.draft,
    session: params.session,
    roles: ['ENTRANCE', 'INSPECTION_ROOM'],
  });
}

export function sessionHasRole(
  session: FactoryVisitSession | null | undefined,
  role: LocationEvidenceRole
): boolean {
  return Boolean(session?.evidence?.some((item) => item.role === role));
}

export function shouldInvalidateVerificationDraft(
  draft: LocationVerificationDraft,
  inspection: Inspection
): boolean {
  if (draft.workflowState === 'STARTED') {
    return false;
  }
  if (
    draft.observedStatusId != null &&
    inspection.inspectionStatusId != null &&
    draft.observedStatusId !== inspection.inspectionStatusId
  ) {
    return true;
  }
  return false;
}

export type FactoryVerificationCaptureReason =
  | 'notApplicable'
  | 'inProgressDraft'
  | 'completeDraft'
  | 'onSiteWithinThreshold'
  | 'offSiteBeyondThreshold'
  | 'gpsUnavailable'
  | 'registeredCoordsMissing';

export type FactoryVerificationCaptureDecision = {
  requiresCapture: boolean;
  reason: FactoryVerificationCaptureReason;
  distanceMetres: number | null;
  distanceThresholdM: number;
  hasDeviceLocation: boolean;
  hasRegisteredCoords: boolean;
  isOnSite: boolean | null;
};

export function evaluateFactoryVerificationCaptureDecision(params: {
  inspection: Inspection | null;
  factoryVerificationEnabled: boolean;
  draft: LocationVerificationDraft | null | undefined;
  deviceLocation: DeviceLocation | null;
  distanceThresholdM: number;
}): FactoryVerificationCaptureDecision {
  const { inspection, factoryVerificationEnabled, draft, deviceLocation, distanceThresholdM } =
    params;
  const registeredCoords = getFactoryRegisteredCoords(inspection);
  const baseDecision = {
    distanceMetres: null as number | null,
    distanceThresholdM,
    hasDeviceLocation: deviceLocation != null,
    hasRegisteredCoords: registeredCoords != null,
    isOnSite: null as boolean | null,
  };
  if (!needsFactoryVerificationFlow(inspection, factoryVerificationEnabled, draft)) {
    return {
      ...baseDecision,
      requiresCapture: false,
      reason: 'notApplicable',
    };
  }
  if (hasInProgressVerificationDraft(draft)) {
    return {
      ...baseDecision,
      requiresCapture: true,
      reason: 'inProgressDraft',
    };
  }
  if (isVerificationDraftComplete(draft)) {
    return {
      ...baseDecision,
      requiresCapture: true,
      reason: 'completeDraft',
    };
  }
  if (!deviceLocation) {
    return {
      ...baseDecision,
      requiresCapture: true,
      reason: 'gpsUnavailable',
    };
  }
  if (!registeredCoords) {
    return {
      ...baseDecision,
      requiresCapture: false,
      reason: 'registeredCoordsMissing',
    };
  }
  const distanceM = haversineDistanceMetres(
    deviceLocation.latitude,
    deviceLocation.longitude,
    registeredCoords.lat,
    registeredCoords.lng
  );
  const roundedDistance = Math.round(distanceM);
  const onSite = distanceM <= distanceThresholdM;
  if (onSite) {
    return {
      ...baseDecision,
      requiresCapture: false,
      reason: 'onSiteWithinThreshold',
      distanceMetres: roundedDistance,
      isOnSite: true,
    };
  }
  return {
    ...baseDecision,
    requiresCapture: true,
    reason: 'offSiteBeyondThreshold',
    distanceMetres: roundedDistance,
    isOnSite: false,
  };
}

export function requiresFactoryVerificationCapture(params: {
  inspection: Inspection | null;
  factoryVerificationEnabled: boolean;
  draft: LocationVerificationDraft | null | undefined;
  deviceLocation: DeviceLocation | null;
  distanceThresholdM: number;
}): boolean {
  return evaluateFactoryVerificationCaptureDecision(params).requiresCapture;
}

export function needsFactoryVerificationFlow(
  inspection: Inspection | null,
  factoryVerificationEnabled: boolean,
  draft: LocationVerificationDraft | null | undefined
): boolean {
  if (
    !inspection ||
    !factoryVerificationEnabled ||
    !inspectionRequiresLocationVerification(inspection)
  ) {
    return false;
  }
  if (draft?.workflowState === 'STARTED') {
    return false;
  }
  if (
    isInspectionStatus(inspection.inspectionStatusId, InspectionStatus.STARTED) ||
    isInspectionStatus(inspection.inspectionStatusId, InspectionStatus.PAUSED)
  ) {
    return false;
  }
  if (isVerificationDraftComplete(draft)) {
    return true;
  }
  return isInspectionStatus(inspection.inspectionStatusId, InspectionStatus.PLANNED);
}

export function resolveVerificationStep(
  stepParam: string | undefined,
  draft: LocationVerificationDraft | null,
  _factorySession?: FactoryVisitSession | null,
  _reuseDays?: number
): FactoryVerificationStep {
  const allowed: FactoryVerificationStep[] = ['entrance', 'saved', 'room', 'complete'];
  if (stepParam === 'session') {
    stepParam = undefined;
  }
  if (stepParam && allowed.includes(stepParam as FactoryVerificationStep)) {
    return stepParam as FactoryVerificationStep;
  }
  if (!draft || !hasEvidenceForRole(draft, 'ENTRANCE')) {
    return 'entrance';
  }
  if (!hasEvidenceForRole(draft, 'INSPECTION_ROOM')) {
    if (isVerificationProgressCommitted(draft)) {
      return 'saved';
    }
    return 'entrance';
  }
  return 'complete';
}

export function buildVerificationResumePath(
  inspectionId: string,
  draft: LocationVerificationDraft | null,
  _factorySession?: FactoryVisitSession | null,
  _reuseDays?: number
): string {
  const step = resolveVerificationStep(undefined, draft);
  return `/inspections/${inspectionId}/verification/${step}`;
}

export function toServerLocationEvidence(evidence: LocationEvidence[]): Array<{
  clientEvidenceId: string;
  role: LocationEvidenceRole;
  latitude?: number;
  longitude?: number;
  accuracy?: number;
  altitude?: number;
  altitudeAccuracy?: number;
  heading?: number;
  speed?: number;
  gpsCaptureSource?: GpsCaptureSource;
  gpsAccuracyPreciseEnough?: boolean;
  connectionType?: string;
  connectionEffectiveType?: string;
  geolocationPermissionState?: string;
  clientUserAgent?: string;
  gpsCapturedAt: string;
  imageUrl?: string;
  factoryId?: string;
  factoryVisitSessionId?: string;
  sourceInspectionId?: string;
  sourceClientEvidenceId?: string;
  isReused?: boolean;
}> {
  return evidence.map((item) => ({
    clientEvidenceId: item.clientEvidenceId,
    role: item.role,
    ...(item.latitude != null ? { latitude: item.latitude } : {}),
    ...(item.longitude != null ? { longitude: item.longitude } : {}),
    ...(item.accuracy != null ? { accuracy: item.accuracy } : {}),
    ...(item.altitude != null ? { altitude: item.altitude } : {}),
    ...(item.altitudeAccuracy != null ? { altitudeAccuracy: item.altitudeAccuracy } : {}),
    ...(item.heading != null ? { heading: item.heading } : {}),
    ...(item.speed != null ? { speed: item.speed } : {}),
    ...(item.gpsCaptureSource ? { gpsCaptureSource: item.gpsCaptureSource } : {}),
    ...(item.gpsAccuracyPreciseEnough != null
      ? { gpsAccuracyPreciseEnough: item.gpsAccuracyPreciseEnough }
      : {}),
    ...(item.connectionType ? { connectionType: item.connectionType } : {}),
    ...(item.connectionEffectiveType
      ? { connectionEffectiveType: item.connectionEffectiveType }
      : {}),
    ...(item.geolocationPermissionState
      ? { geolocationPermissionState: item.geolocationPermissionState }
      : {}),
    ...(item.clientUserAgent ? { clientUserAgent: item.clientUserAgent } : {}),
    gpsCapturedAt: item.gpsCapturedAt,
    ...(item.imageUrl ? { imageUrl: item.imageUrl } : {}),
    ...(item.factoryId ? { factoryId: item.factoryId } : {}),
    ...(isUuid(item.factoryVisitSessionId)
      ? { factoryVisitSessionId: item.factoryVisitSessionId }
      : {}),
    ...(item.sourceInspectionId ? { sourceInspectionId: item.sourceInspectionId } : {}),
    ...(item.sourceClientEvidenceId ? { sourceClientEvidenceId: item.sourceClientEvidenceId } : {}),
    ...(item.isReused ? { isReused: true } : {}),
  }));
}

export function getPrimaryEntranceCoordinates(draft: LocationVerificationDraft): {
  latitude: number;
  longitude: number;
} | null {
  const entrance = getEvidenceForRole(draft, 'ENTRANCE')[0];
  if (!entrance || entrance.latitude == null || entrance.longitude == null) {
    return null;
  }
  return { latitude: entrance.latitude, longitude: entrance.longitude };
}

export function mapSessionEvidenceToLocationEvidence(params: {
  sessionEvidence: FactoryVisitSessionEvidence;
  inspectionId: string;
  factoryId: string;
  serverFactoryVisitSessionId?: string;
  imageDataKey?: string;
}): LocationEvidence {
  const clientEvidenceId = params.sessionEvidence.clientEvidenceId;
  return {
    clientEvidenceId,
    role: params.sessionEvidence.role,
    latitude: params.sessionEvidence.latitude,
    longitude: params.sessionEvidence.longitude,
    accuracy: params.sessionEvidence.accuracy,
    altitude: params.sessionEvidence.altitude ?? null,
    altitudeAccuracy: params.sessionEvidence.altitudeAccuracy ?? null,
    heading: params.sessionEvidence.heading ?? null,
    speed: params.sessionEvidence.speed ?? null,
    gpsCaptureSource: params.sessionEvidence.gpsCaptureSource ?? null,
    gpsAccuracyPreciseEnough: params.sessionEvidence.gpsAccuracyPreciseEnough ?? null,
    connectionType: params.sessionEvidence.connectionType ?? null,
    connectionEffectiveType: params.sessionEvidence.connectionEffectiveType ?? null,
    geolocationPermissionState: params.sessionEvidence.geolocationPermissionState ?? null,
    clientUserAgent: params.sessionEvidence.clientUserAgent ?? null,
    gpsCapturedAt: params.sessionEvidence.gpsCapturedAt,
    contentType: params.sessionEvidence.contentType ?? 'image/jpeg',
    localImageKey: params.imageDataKey ?? params.sessionEvidence.clientEvidenceId,
    uploadState: params.sessionEvidence.imageUrl ? 'UPLOADED' : 'PENDING',
    imageUrl: params.sessionEvidence.imageUrl,
    factoryId: params.factoryId,
    ...(params.serverFactoryVisitSessionId
      ? { factoryVisitSessionId: params.serverFactoryVisitSessionId }
      : {}),
    sourceInspectionId: params.sessionEvidence.sourceInspectionId,
    sourceClientEvidenceId: params.sessionEvidence.clientEvidenceId,
    isReused: true,
    hasPhoto: true,
  };
}

export function copyReusableSessionEvidenceToDraft(params: {
  draft: LocationVerificationDraft;
  session: FactoryVisitSession;
  roles: LocationEvidenceRole[];
}): LocationVerificationDraft {
  const serverSessionId = resolveServerFactoryVisitSessionId(params.session);
  const copiedEvidence = params.roles.flatMap((role) =>
    params.session.evidence
      .filter((item) => item.role === role)
      .filter((item) => item.sourceInspectionId !== params.draft.inspectionId)
      .map((item) =>
        mapSessionEvidenceToLocationEvidence({
          sessionEvidence: item,
          inspectionId: params.draft.inspectionId,
          factoryId: params.session.factoryId,
          serverFactoryVisitSessionId: serverSessionId,
        })
      )
  );
  const nextEvidence = [...params.draft.evidence];
  for (const evidence of copiedEvidence) {
    const alreadyExists = nextEvidence.some(
      (item) => item.sourceClientEvidenceId === evidence.sourceClientEvidenceId
    );
    if (!alreadyExists) {
      nextEvidence.push(evidence);
    }
  }
  const hasEntrance = hasEvidenceForRole({ ...params.draft, evidence: nextEvidence }, 'ENTRANCE');
  const hasRoom = hasEvidenceForRole(
    { ...params.draft, evidence: nextEvidence },
    'INSPECTION_ROOM'
  );
  let workflowState = params.draft.workflowState;
  if (hasEntrance && hasRoom) {
    workflowState = 'ROOM_CAPTURED';
  } else if (hasEntrance) {
    workflowState = 'ENTRANCE_CAPTURED';
  }
  return {
    ...params.draft,
    factoryId: params.session.factoryId,
    factoryVisitSessionId: resolveServerFactoryVisitSessionId(params.session),
    evidence: nextEvidence,
    workflowState,
    isProgressCommitted: true,
    updatedAt: new Date().toISOString(),
  };
}
